import { useMutation } from '@apollo/client';
import { GET_COMMUNITY_PROMPTS, UPDATE_PROMPT_PROD_VERSION } from '@communities/prompt-management/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useState, useCallback } from 'react';

export interface UpdatePromptProdVersionInput {
  version: string;
}

interface UpdatePromptProdVersionResponse {
  updatePromptProdVersion: {
    success: boolean;
    message: string;
  };
}

export function useUpdateProdPromptVersion() {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [updatePromptProdVersionMutation, { loading }] = useMutation<UpdatePromptProdVersionResponse>(UPDATE_PROMPT_PROD_VERSION);

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const updatePromptProdVersion = async (communityId: string, input: UpdatePromptProdVersionInput): Promise<boolean> => {
    try {
      track('UpdateProdPromptVersionInitiated', {
        communityId,
        promptVersion: input.version,
      });

      await updatePromptProdVersionMutation({
        variables: {
          communityId,
          input,
        },
        refetchQueries: communityId ? [{ query: GET_COMMUNITY_PROMPTS, variables: { communityId } }] : [],
        awaitRefetchQueries: true,
      });

      track('UpdateProdPromptVersionSucceeded', {
        communityId,
        promptVersion: input.version,
      });

      return true;
    } catch (error) {
      track('UpdateProdPromptVersionFailed', {
        communityId,
        promptVersion: input.version,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    updatePromptProdVersion,
    loading,
    errors,
    resetErrors,
  };
}

export default useUpdateProdPromptVersion;
